import type { ReactElement } from 'react';

import { ChatPanel as KitChatPanel } from '@govard/ai-chat-kit/react';
import { useTranslation } from '@govard/i18n-core/react';
import type { Scope } from '@govard/tenant-core';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@govard/ui-primitives';

import { AiApprovalCard } from './AiApprovalCard.js';
import { useDealChat } from '../hooks/useDealChat.js';
import type { ApprovalItem } from '../api/approvals.client.js';

export interface DealChatPanelProps {
  scope: Scope | null;
  dealId: string;
}

interface ApprovalCardEntry {
  approval: ApprovalItem;
  summary: string;
  diff: string;
}

function parseApprovalCardEntry(content: string): ApprovalCardEntry | null {
  let parsed: unknown;

  try {
    parsed = JSON.parse(content);
  } catch {
    return null;
  }

  if (typeof parsed !== 'object' || parsed === null) {
    return null;
  }

  const record = parsed as Record<string, unknown>;
  if (
    typeof record.approvalId !== 'string'
    || typeof record.kind !== 'string'
    || typeof record.status !== 'string'
    || typeof record.toolName !== 'string'
    || typeof record.summary !== 'string'
    || typeof record.diff !== 'string'
    || typeof record.expiresAt !== 'string'
    || typeof record.args !== 'object'
    || record.args === null
  ) {
    return null;
  }

  return {
    approval: {
      id: record.approvalId,
      kind: record.kind as ApprovalItem['kind'],
      status: record.status as ApprovalItem['status'],
      payload: {
        toolName: record.toolName,
        args: record.args as Record<string, unknown>,
      },
      expiresAt: record.expiresAt,
    },
    summary: record.summary,
    diff: record.diff,
  };
}

export function DealChatPanel({ scope, dealId }: DealChatPanelProps): ReactElement {
  const { t } = useTranslation();
  const agent = useDealChat(scope, dealId);
  const followUpPrompt = t('crm.chat.quick_follow_up_prompt', { dealId });

  return (
    <Card data-testid="chatpanel-container" className="h-full border-0 bg-transparent shadow-none">
      <CardHeader className="border-b border-stone-200 px-5 py-4">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-[13px] font-semibold text-stone-900">{t('crm.chat.title')}</CardTitle>
          <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
            {t('crm.chat.ready')}
          </span>
        </div>
      </CardHeader>
      <CardContent className="flex h-[calc(100%-53px)] flex-col gap-4 px-5 py-4">
        <div className="rounded-xl border border-stone-200 bg-white p-4">
          <p className="mb-3 text-[12px] leading-5 text-stone-500">
            {t('crm.chat.context_hint')}
          </p>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="w-full justify-center border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-100"
            disabled={agent.isStreaming || scope === null}
            onClick={() => {
              void agent.send(followUpPrompt);
            }}
          >
            {t('crm.chat.quick_follow_up')}
          </Button>
        </div>
        <div className="rounded-xl border border-stone-200 bg-white p-4">
          <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-stone-500">
            {t('crm.chat.suggestions_title')}
          </p>
          <div className="space-y-2">
            <button
              type="button"
              className="w-full rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 text-left text-[12px] font-medium text-stone-700 transition hover:border-amber-200 hover:bg-amber-50"
              disabled={agent.isStreaming || scope === null}
              onClick={() => {
                void agent.send(t('crm.chat.suggestion_next_step_prompt', { dealId }));
              }}
            >
              {t('crm.chat.suggestion_next_step')}
            </button>
            <button
              type="button"
              className="w-full rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 text-left text-[12px] font-medium text-stone-700 transition hover:border-amber-200 hover:bg-amber-50"
              disabled={agent.isStreaming || scope === null}
              onClick={() => {
                void agent.send(t('crm.chat.suggestion_risks_prompt', { dealId }));
              }}
            >
              {t('crm.chat.suggestion_risks')}
            </button>
          </div>
        </div>
        <KitChatPanel
          agent={agent}
          variant="sidebar"
          placeholder={t('crm.chat.placeholder')}
          submitLabel={t('crm.chat.send')}
          renderMessageContent={(message) => {
            const approvalCard = message.role === 'tool' ? parseApprovalCardEntry(message.content) : null;
            if (approvalCard === null) {
              return <span className="ack-chat-panel__content">{message.content}</span>;
            }

            return (
              <AiApprovalCard
                approval={approvalCard.approval}
                summary={approvalCard.summary}
                diff={approvalCard.diff}
              />
            );
          }}
          testIds={{ root: 'chatpanel-kit', input: 'chatpanel-input', sendButton: 'chatpanel-send' }}
        />
        {agent.usage ? (
          <p className="text-xs text-muted-foreground">
            {t('crm.chat.usage', { cost: agent.usage.costRub.toFixed(1) })}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
